import { useState, type FormEvent } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { createProject } from '@/features/projects/projects-api'
import { projectsQueryKey } from '@/features/projects/projects-query-keys'
import { Button } from '@/shared/components/ui/button'
import { Field } from '@/shared/components/ui/field'
import { Input } from '@/shared/components/ui/input'
import { Modal } from '@/shared/components/ui/modal'
import { Textarea } from '@/shared/components/ui/textarea'
import type { Project } from '@/shared/types/domain'

interface CreateProjectModalProps {
  open: boolean
  onClose: () => void
  onCreated?: (project: Project) => void
}

export function CreateProjectModal({ open, onClose, onCreated }: CreateProjectModalProps) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({ name: '', description: '' })

  const createMutation = useMutation({
    mutationFn: () =>
      createProject({ name: form.name.trim(), description: form.description.trim() }),
    onSuccess: (project) => {
      void queryClient.invalidateQueries({ queryKey: projectsQueryKey })
      setForm({ name: '', description: '' })
      onClose()
      onCreated?.(project)
    },
  })

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!form.name.trim()) {
      return
    }

    createMutation.mutate()
  }

  return (
    <Modal
      description="Название обязательно, описание — опционально."
      onClose={onClose}
      open={open}
      title="Новый проект"
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Field label="Название" required>
          {(props) => (
            <Input
              {...props}
              onChange={(event) => setForm((c) => ({ ...c, name: event.target.value }))}
              placeholder="Например: Мой магазин"
              required
              value={form.name}
            />
          )}
        </Field>
        <Field label="Описание">
          {(props) => (
            <Textarea
              {...props}
              onChange={(event) => setForm((c) => ({ ...c, description: event.target.value }))}
              value={form.description}
            />
          )}
        </Field>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} type="button" variant="ghost">
            Отмена
          </Button>
          <Button loading={createMutation.isPending} type="submit">
            Создать
          </Button>
        </div>
      </form>
    </Modal>
  )
}
